import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, History, MessageSquarePlus, Pause, Play, Square } from 'lucide-react';
import { agentTimeline, cancelRun, getAgent, getThread, listThreads, pauseAgent, resumeAgent, sendMessage, startSession } from '@mkcore/agents/schema';
import { Badge, Button, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, ErrorState, Field, Input, PageHeader, Skeleton, Textarea } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime, formatMicros } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { formatDuration } from '@/features/admin/job-status';
import { ChatButton } from '@/features/agents/chat';
import { AgentLive, cockpitQuery } from '@/features/agents/cockpit';
import { RevisionsDialog } from '@/features/agents/governance';
import { ProfileSyncCard } from '@/features/agents/profile-sync';
import { AgentRoutines } from '@/features/agents/routines';

export const Route = createFileRoute('/_app/agents/$id')({ component: AgentPage });

const statusTone = { active: 'success', paused: 'warning', terminated: 'neutral' } as const;
const entryTone: Record<string, 'success' | 'danger' | 'warning' | 'info' | 'neutral' | 'outline'> = {
  succeeded: 'success',
  done: 'success',
  failed: 'danger',
  rejected: 'danger',
  cancelled: 'neutral',
  released: 'neutral',
  expired: 'warning',
  running: 'info',
  active: 'info',
  in_review: 'info',
  saved: 'outline',
};

/**
 * One agent (CO-1241, CO-1252, CO-1259): what it does now, its sessions with their messages (start one, send an
 * instruction, stop a run), its routines (CO-1250) and its work history with totals. Live through the SSE stream, like the cockpit.
 */
function AgentPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const canUpdate = useCan('agents:update');
  const agent = useQuery({ queryKey: ['agent', 'agent', id], queryFn: () => call(getAgent, { id }) });
  const cockpit = useQuery(cockpitQuery);
  const [starting, setStarting] = useState(false);
  const [revisions, setRevisions] = useState(false);
  const invalidate = [['agent']];
  const pause = useActionMutation(pauseAgent, { invalidate });
  const resume = useActionMutation(resumeAgent, { invalidate });
  const stop = useActionMutation(cancelRun, { invalidate, success: t('agents.page.stopping') });
  if (agent.isError) return <ErrorState title={t('errors.generic')} description={message(agent.error)} />;
  if (!agent.data) return <Skeleton className="h-96" />;
  const a = agent.data;
  const live = cockpit.data?.agents.find((x) => x.id === id);
  return (
    <>
      <Link to="/agents" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('agents.title')}
      </Link>
      <PageHeader
        eyebrow={a.role}
        title={a.name}
        description={
          <span className="inline-flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={statusTone[a.status]}>{t(`agents.status.${a.status}`)}</Badge>
            <span>{t(`agents.runtimes.${a.runtime}`)}</span>
            {a.autonomy === 'propose' && <Badge tone="outline">{t('agents.governance.autonomy.propose')}</Badge>}
            {a.managerName && <span>{t('agents.governance.reportsTo', { name: a.managerName })}</span>}
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {a.status !== 'terminated' && <ChatButton agentId={id} />}
            <Button variant="secondary" onClick={() => setRevisions(true)}>
              <History /> {t('agents.governance.revisions', { number: a.revision })}
            </Button>
            {canUpdate && a.status === 'active' && (
              <Button variant="secondary" loading={pause.isPending} onClick={() => pause.mutate({ input: { id, reason: '' } })}>
                <Pause /> {t('agents.pause')}
              </Button>
            )}
            {canUpdate && a.status === 'paused' && (
              <Button variant="secondary" loading={resume.isPending} onClick={() => resume.mutate({ input: { id } })}>
                <Play /> {t('agents.resume')}
              </Button>
            )}
            {canUpdate && a.status === 'active' && (
              <Button onClick={() => setStarting(true)}>
                <MessageSquarePlus /> {t('agents.page.startSession')}
              </Button>
            )}
          </div>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="grid content-start gap-4">
          {live && (
            <Card className="grid gap-2 p-4" role="region" aria-label={t('agents.page.now')}>
              <h2 className="font-bold">{t('agents.page.now')}</h2>
              <AgentLive a={live} currency={cockpit.data!.totals.currency} />
              {canUpdate &&
                live.running.map((r) => (
                  <Button key={r.runId} size="sm" variant="ghost" className="justify-self-start" loading={stop.isPending && stop.variables?.input.id === r.runId} onClick={() => stop.mutate({ input: { id: r.runId } })}>
                    <Square /> {t('agents.page.stop', { task: r.task })}
                  </Button>
                ))}
            </Card>
          )}
          <Threads agentId={id} canUpdate={canUpdate} />
        </div>
        <div className="grid content-start gap-4">
          {a.runtime === 'hermes' && a.profile && <ProfileSyncCard agentId={id} profile={a.profile} canUpdate={canUpdate} active={a.status !== 'terminated'} />}
          <AgentRoutines agentId={id} canUpdate={canUpdate} />
          <Timeline agentId={id} />
        </div>
      </div>
      {starting && <StartSessionDialog agentId={id} onClose={() => setStarting(false)} />}
      {revisions && <RevisionsDialog agent={a} onClose={() => setRevisions(false)} />}
    </>
  );
}

function Threads({ agentId, canUpdate }: { agentId: string; canUpdate: boolean }) {
  const { t } = useTranslation();
  const threads = useQuery({ queryKey: ['agent', 'thread', 'list', agentId], queryFn: () => call(listThreads, { agentId, limit: 20 }) });
  const [open, setOpen] = useState<string | null>(null);
  const rows = threads.data?.items ?? [];
  const current = open ?? rows[0]?.id ?? null;
  return (
    <Card className="grid gap-3 p-4" role="region" aria-label={t('agents.page.threads')}>
      <h2 className="font-bold">{t('agents.page.threads')}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-fg-3">{t('agents.page.noThreads')}</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1">
            {rows.map((th) => (
              <Button key={th.id} size="sm" variant={th.id === current ? 'primary' : 'secondary'} onClick={() => setOpen(th.id)}>
                {th.title}
                {th.activeRunId && <Badge tone="info">{t('agents.cockpit.runningNow')}</Badge>}
              </Button>
            ))}
          </div>
          {current && <ThreadView threadId={current} canUpdate={canUpdate} />}
        </>
      )}
    </Card>
  );
}

function ThreadView({ threadId, canUpdate }: { threadId: string; canUpdate: boolean }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['agent', 'thread', threadId], queryFn: () => call(getThread, { id: threadId }) });
  const [body, setBody] = useState('');
  const send = useActionMutation(sendMessage, { invalidate: [['agent']], success: t('agents.page.sent'), onSuccess: () => setBody('') });
  if (!q.data) return <Skeleton className="h-32" />;
  const th = q.data;
  const ws = th.workState;
  return (
    <div className="grid gap-2">
      {(ws.phase || ws.step || ws.nextAction || (ws.blockers?.length ?? 0) > 0) && (
        <p className="rounded-md bg-surface-alt p-2 text-xs">
          {[ws.phase, ws.step].filter(Boolean).join(' · ')}
          {ws.blockers?.map((b) => (
            <span key={b} className="block text-warning">
              {t('agents.cockpit.blocked', { what: b })}
            </span>
          ))}
          {ws.nextAction && <span className="block text-fg-3">{t('agents.cockpit.next', { what: ws.nextAction })}</span>}
        </p>
      )}
      <ol className="grid max-h-96 gap-2 overflow-auto">
        {th.messages.map((m) => (
          <li key={m.id} className={`rounded-md border p-2 text-sm ${m.authorKind === 'human' ? 'border-primary/40' : 'border-line'}`}>
            <span className="flex flex-wrap items-center gap-2 text-xs text-fg-3">
              <strong className="text-fg-1">{m.authorName}</strong>
              <Badge tone={m.kind === 'question' && !m.answeredAt ? 'warning' : 'outline'}>{t(`agents.page.kinds.${m.kind}`)}</Badge>
              {formatDateTime(m.createdAt)}
              {m.approvalId && !m.answeredAt && (
                <Link to="/approvals" className="underline">
                  {t('agents.page.inInbox')}
                </Link>
              )}
            </span>
            <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
          </li>
        ))}
      </ol>
      {canUpdate && th.status === 'open' && (
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate({ input: { threadId: th.id, kind: 'instruction', body: body.trim() } });
          }}
        >
          <Field label={t('agents.page.instruction')} hint={t('agents.page.instructionHint')}>
            {(p) => <Textarea {...p} rows={3} value={body} onChange={(e) => setBody(e.target.value)} />}
          </Field>
          <Button type="submit" className="justify-self-start" loading={send.isPending} disabled={!body.trim()}>
            {t('agents.page.send')}
          </Button>
        </form>
      )}
    </div>
  );
}

function Timeline({ agentId }: { agentId: string }) {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const q = useQuery({ queryKey: ['agent', 'timeline', agentId, days], queryFn: () => call(agentTimeline, { id: agentId, days }) });
  const s = q.data?.stats;
  return (
    <Card className="grid content-start gap-3 p-4" role="region" aria-label={t('agents.page.timeline')}>
      <div className="flex items-center gap-2">
        <h2 className="flex-1 font-bold">{t('agents.page.timeline')}</h2>
        {[7, 30, 90].map((d) => (
          <Button key={d} size="sm" variant={d === days ? 'primary' : 'ghost'} onClick={() => setDays(d)}>
            {t('agents.page.days', { count: d })}
          </Button>
        ))}
      </div>
      {s && (
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-fg-3">{t('agents.page.runs')}</dt>
          <dd>{t('agents.page.runStats', { runs: s.runs, succeeded: s.succeeded, failed: s.failed })}</dd>
          <dt className="text-fg-3">{t('agents.page.claims')}</dt>
          <dd>{t('agents.page.claimStats', { done: s.claimsDone, rejected: s.claimsRejected })}</dd>
          <dt className="text-fg-3">{t('agents.page.cost')}</dt>
          <dd>{formatMicros(s.costMicros, s.currency)}</dd>
          <dt className="text-fg-3">{t('agents.page.avgDuration')}</dt>
          <dd>{s.avgDurationMs !== null ? formatDuration(s.avgDurationMs) : '—'}</dd>
        </dl>
      )}
      {q.data && q.data.entries.length === 0 && <EmptyState title={t('agents.page.noHistory')} />}
      <ol className="grid gap-2">
        {(q.data?.entries ?? []).map((e, i) => (
          <li key={`${e.kind}-${e.at}-${i}`} className="grid gap-0.5 text-sm">
            <span className="flex flex-wrap items-center gap-2">
              <Badge tone={entryTone[e.status] ?? 'outline'}>{t(`agents.page.entry.${e.kind}`)}</Badge>
              {e.path && e.kind === 'run' ? (
                <Link to="/agents/runs/$id" params={{ id: e.path.split('/').pop()! }} className="truncate underline">
                  {e.title}
                </Link>
              ) : e.path && e.kind === 'claim' ? (
                <Link to="/agents/tasks/$id" params={{ id: e.path.split('/').pop()! }} className="truncate underline">
                  {e.title}
                </Link>
              ) : (
                <span className="truncate">{e.title}</span>
              )}
            </span>
            <span className="text-xs text-fg-3">
              {[formatDateTime(e.at), e.status, e.durationMs !== null ? formatDuration(e.durationMs) : null, e.costMicros ? formatMicros(e.costMicros, s?.currency) : null].filter(Boolean).join(' · ')}
            </span>
            {e.detail && <span className="line-clamp-2 text-xs text-fg-2">{e.detail}</span>}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function StartSessionDialog({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [instruction, setInstruction] = useState('');
  const start = useActionMutation(startSession, { invalidate: [['agent']], success: t('agents.page.started'), onSuccess: onClose });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')} className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('agents.page.startSession')}</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            start.mutate({ input: { id: agentId, instruction: instruction.trim(), ...(title.trim() && { title: title.trim() }) } });
          }}
        >
          <Field label={t('agents.page.sessionTitle')}>{(p) => <Input {...p} maxLength={300} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
          <Field label={t('agents.page.instruction')}>{(p) => <Textarea {...p} required rows={6} value={instruction} onChange={(e) => setInstruction(e.target.value)} />}</Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={start.isPending} disabled={!instruction.trim()}>
              {t('agents.page.start')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
